export const SESSIONS = {
  "pneumonia-cap": {
    id: "pneumonia-cap",
    topic: "Community-Acquired Pneumonia (CAP)",
    emoji: "🫁",
    color: "from-blue-600 to-cyan-700",
    slides: [
      {
        title: "Community-Acquired Pneumonia",
        subtitle: "Overview",
        content: `Pneumonia is an acute lower respiratory tract infection causing inflammation of the lung parenchyma.

CAP is defined as pneumonia acquired outside of hospital or within 48 hours of admission.

📊 Epidemiology
• 5–11 cases per 1,000 adults per year in the UK
• Leading infectious cause of death worldwide
• Higher incidence in winter months
• Increased risk: elderly, immunocompromised, comorbidities`,
      },
      {
        title: "Causative Organisms",
        subtitle: "Know Your Enemy",
        content: `🥇 Most Common
• Streptococcus pneumoniae (30–40%) — most common across all age groups

🔵 Atypical Organisms (need macrolide or tetracycline)
• Mycoplasma pneumoniae — especially young adults, 3–4 year epidemics
• Legionella pneumophila — hotels, air conditioning, water systems
• Chlamydophila pneumoniae

🔴 Others
• Haemophilus influenzae — especially in COPD
• Staphylococcus aureus — post-influenza, severe disease
• Gram-negatives — nursing home, aspiration pneumonia`,
      },
      {
        title: "Clinical Features",
        subtitle: "Recognising CAP",
        content: `🌡️ Typical Symptoms
• Fever, rigors, night sweats
• Productive cough — rust-coloured sputum (pneumococcal)
• Pleuritic chest pain
• Dyspnoea
• Confusion — often the ONLY feature in elderly patients

🔍 Signs on Examination
• Tachypnoea (>20/min) — early and sensitive sign
• Tachycardia, reduced O₂ saturation
• Dullness to percussion
• Bronchial breathing, coarse crackles

⚠️ Atypical Features (Mycoplasma / Legionella)
• Dry cough, headache, myalgia
• GI symptoms and hyponatraemia (Legionella)
• Erythema multiforme rash (Mycoplasma)`,
      },
      {
        title: "Investigations",
        subtitle: "Working Up the Patient",
        content: `📸 Chest X-Ray — ESSENTIAL
• Lobar consolidation (typical)
• Bilateral patchy infiltrates (atypical or viral)
• Required to confirm the diagnosis

🩸 Bloods
• FBC — leucocytosis (WBC >15 suggests bacterial)
• CRP — severity marker
• U&E — urea is part of CURB-65
• LFTs — Legionella causes hepatitis
• Blood cultures ×2 if hospitalised or severe

🦠 Microbiology
• Sputum MC&S — before antibiotics if possible
• Pneumococcal urinary antigen
• Legionella urinary antigen — if suspected
• Throat swab / NPS if viral cause suspected`,
      },
      {
        title: "CURB-65 Severity Score",
        subtitle: "Who Needs to Be Admitted?",
        content: `One point for each criterion:

🧠 C — Confusion (new onset)
🩸 U — Urea > 7 mmol/L
💨 R — Respiratory rate ≥ 30/min
❤️ B — Blood pressure: systolic <90 or diastolic ≤60 mmHg
🎂 65 — Age ≥ 65 years

📊 Score Interpretation
• 0–1 → Treat at HOME (mortality <3%)
• 2    → Consider HOSPITAL admission
• 3–5 → HOSPITAL required; consider ICU if 4–5 (mortality up to 40%)

⚠️ Clinical judgement always overrides the score — consider O₂ saturation, social factors, ability to take oral medication`,
      },
      {
        title: "Treatment Guidelines",
        subtitle: "BTS / NICE Recommendations",
        content: `🏠 Mild CAP (CURB-65 0–1) — Community Treatment
• Amoxicillin 500mg TDS × 5 days (first-line)
• Penicillin allergy: Doxycycline 200mg loading then 100mg OD
• Add Clarithromycin 500mg BD if atypical suspected

🏥 Moderate CAP (CURB-65 2) — Hospital
• Amoxicillin 500mg–1g TDS PO + Clarithromycin 500mg BD PO
• Switch to IV if unable to tolerate orally

🚨 Severe CAP (CURB-65 3–5) — Hospital / ICU
• Co-amoxiclav 1.2g TDS IV + Clarithromycin 500mg BD IV
• Penicillin allergy: Levofloxacin 500mg BD IV
• Review and step down to oral at 48h if improving

⏱️ Antibiotic Timing
• Within 4 hours of diagnosis for all CAP
• Within 1 hour if CURB-65 ≥ 3`,
      },
    ],
    questions: [
      {
        question: "What is the most common causative organism of Community-Acquired Pneumonia?",
        options: [
          { id: "a", text: "Mycoplasma pneumoniae" },
          { id: "b", text: "Streptococcus pneumoniae" },
          { id: "c", text: "Haemophilus influenzae" },
          { id: "d", text: "Legionella pneumophila" },
        ],
        correct: "b",
        explanation: "Streptococcus pneumoniae causes 30–40% of CAP and is the most common pathogen across all age groups. It typically presents with sudden onset fever, rigors, and rust-coloured sputum with lobar consolidation on CXR.",
      },
      {
        question: "A 72-year-old patient presents with CAP. She is confused, urea is 10 mmol/L, respiratory rate is 35/min, and BP is 80/50. What is her CURB-65 score?",
        options: [
          { id: "a", text: "3" },
          { id: "b", text: "4" },
          { id: "c", text: "5" },
          { id: "d", text: "2" },
        ],
        correct: "c",
        explanation: "She scores 5 (maximum): C=1 (confused), U=1 (urea 10 >7), R=1 (RR 35 ≥30), B=1 (systolic 80 <90), 65=1 (age 72). A CURB-65 of 4–5 carries up to 40% mortality and requires urgent hospital admission with consideration of ICU.",
      },
      {
        question: "What is the first-line antibiotic for mild CAP (CURB-65 0–1) in a patient with no penicillin allergy?",
        options: [
          { id: "a", text: "Co-amoxiclav 625mg TDS" },
          { id: "b", text: "Levofloxacin 500mg BD" },
          { id: "c", text: "Amoxicillin 500mg TDS for 5 days" },
          { id: "d", text: "Clarithromycin 500mg BD alone" },
        ],
        correct: "c",
        explanation: "Amoxicillin 500mg three times daily for 5 days is first-line for mild CAP per BTS/NICE guidelines. Co-amoxiclav is not recommended for mild CAP — it's broader spectrum than needed. Clarithromycin alone does not adequately cover typical organisms. Levofloxacin is reserved for penicillin-allergic patients with moderate/severe disease.",
      },
      {
        question: "Which combination of features should make you suspect Legionella over pneumococcal pneumonia?",
        options: [
          { id: "a", text: "Rust-coloured sputum and lobar consolidation on CXR" },
          { id: "b", text: "GI symptoms, hyponatraemia, and recent hotel or spa stay" },
          { id: "c", text: "High fever, rigors, and pleuritic chest pain" },
          { id: "d", text: "Erythema multiforme rash and dry cough" },
        ],
        correct: "b",
        explanation: "Legionella classically presents with GI symptoms (diarrhoea, vomiting), hyponatraemia, and abnormal LFTs alongside respiratory symptoms. Key epidemiological clues: recent hotel, spa, or cruise ship exposure (contaminated water systems). Rust-coloured sputum and rigors suggest pneumococcal. Erythema multiforme is associated with Mycoplasma.",
      },
      {
        question: "Which single investigation is ESSENTIAL to confirm a diagnosis of pneumonia?",
        options: [
          { id: "a", text: "FBC showing leucocytosis" },
          { id: "b", text: "Elevated CRP" },
          { id: "c", text: "Positive sputum culture" },
          { id: "d", text: "Chest X-ray showing consolidation" },
        ],
        correct: "d",
        explanation: "A chest X-ray showing consolidation or new infiltrates is required to confirm the diagnosis. FBC and CRP support the diagnosis but are non-specific. Sputum cultures are often negative even in confirmed bacterial pneumonia and take days to result. The CXR must be documented before starting antibiotics in hospitalised patients.",
      },
      {
        question: "According to BTS/NICE guidelines, within what time frame should antibiotics be started in SEVERE CAP (CURB-65 ≥3)?",
        options: [
          { id: "a", text: "Within 4 hours" },
          { id: "b", text: "Within 6 hours" },
          { id: "c", text: "Within 2 hours" },
          { id: "d", text: "Within 1 hour" },
        ],
        correct: "d",
        explanation: "For severe CAP (CURB-65 ≥3), antibiotics should be started within 1 hour of diagnosis — this is a quality indicator measured in UK hospitals. For mild and moderate CAP the target is within 4 hours. Delays are associated with significantly increased mortality in severe pneumonia.",
      },
    ],
  },
};

export const SESSION_LIST = Object.values(SESSIONS);
